/**
 * Speaker-profile gate for speech projects (Phase B enforcement).
 *
 * On projects with audio QC enabled, the annotator must COMPLETE the
 * speaker-profile step before labeling — completing may mean leaving
 * every field blank and declining the sharing consent; the deliberate
 * choice is what's required, never the disclosure. Mirrors
 * ConsentGate: this gate is the UX; the server re-checks on every
 * annotation/draft create (audio_qc/enforcement.py), so bypassing the
 * gate just moves the wall.
 */
import { useCallback, useEffect, useState } from "react";
import { UserProfileSpeaker } from "../../pages/UserProfile/UserProfileSpeaker";
import { useTranslation } from "../../i18n/LocaleProvider";

export const SpeakerGate = ({ project, children }) => {
  const { t } = useTranslation();
  // null = still checking; true = gate satisfied; false = show gate.
  const [passed, setPassed] = useState(null);
  const isSpeechProject = !!project?.audio_qc_enabled;

  const check = useCallback(async () => {
    try {
      const r = await fetch("/api/current-user/speaker-profile/", {
        credentials: "same-origin",
        cache: "no-store",
      });
      const b = await r.json();
      setPassed(!!b.exists);
    } catch {
      // Offline / transient: don't lock the user out on a failed
      // check — the server-side gate is the real wall.
      setPassed(true);
    }
  }, []);

  useEffect(() => {
    if (!isSpeechProject) {
      setPassed(true);
      return;
    }
    check();
  }, [isSpeechProject, check]);

  if (passed === null) return null;
  if (passed) return children;

  return (
    <div style={{ maxWidth: 720, margin: "32px auto", padding: "0 24px" }}>
      <h3 style={{ margin: "0 0 6px" }}>{t("components.speaker_gate.title")}</h3>
      <p
        style={{
          margin: "0 0 8px",
          fontSize: 14,
          lineHeight: 1.5,
          color: "var(--color-neutral-content-subtler, #475569)",
        }}
      >
        {t("components.speaker_gate.body")}
      </p>
      <UserProfileSpeaker onSaved={() => setPassed(true)} />
    </div>
  );
};
